import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Property } from '../../../models/property';
import { UserAuctionAuthorizationStatusResponse } from '../../../responses/userAuctionAuthorizationStatusResponse';
import { DocumentTemplate } from '../../../models/documentTemplate';
import { GeneralDataService } from '../../../services/GeneralDataService';
import { take } from 'rxjs';
import { Result } from '../../../models/result';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PropertyService } from '../../../services/PropertyService';
import { VaultDocument } from '../../../models/vaultDocument';
import { VaultDocumentsResponse } from '../../../responses/vaultDocumentsResponse';
import { AuthenticationService } from '../../../services/AuthenticationService';

@Component({ standalone: false,
  selector: 'auction-authorization-dialog',
  templateUrl: 'auction-authorization-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['auction-authorization-dialog.component.scss']
})
export class AuctionAuthorizationDialogComponent {
  public title: string= "Action item(s) required before you can bid on this auction";
  public cancelButtonText = "Close";

  public busy:boolean=false;
  public documentTemplate?:DocumentTemplate;

  private property!:Property;
  private authorizationStatus!:UserAuctionAuthorizationStatusResponse;
  private isPrivateAuction:boolean=false;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<AuctionAuthorizationDialogComponent>, private gdService:GeneralDataService, private propertyService:PropertyService, private authService:AuthenticationService, private snackBar: MatSnackBar) {
    if(data){
      this.property = data.property;
      this.authorizationStatus = data.authorizationStatus;
      this.isPrivateAuction = data.isPrivateAuction;
      if(this.needsPrePurchaseAgreement && !this.hasPrePurchaseAgreement){
        this.gdService.getDocumentTemplateById(this.property!.auction!.purchaseAgreementTemplateId!).pipe(take(1)).subscribe(dtArray=>{
          if(dtArray?.length>0) this.documentTemplate=dtArray[0]; //expecting only max one template here
          //now create the dynamic form based on the template placeholders array
        });
      }
    }
  }

  get openActionItemsCount(){
    let count:number=0;
    if(this.needsFinancialAuthorization && !this.hasFinancialAuthorization) count++;
    if(this.needsPrePurchaseAgreement && !this.hasPrePurchaseAgreement) count++;
    return count; 
  }

  get needsFinancialAuthorization(){
    return this.property?.auction?.amountToAuthorize && this.property.auction.amountToAuthorize>0 && !this.isPrivateAuction;
  }

  get hasFinancialAuthorization(){
    return this.authorizationStatus?.financialAuthorizationExists;
  }

  get financialAuthorizationExpanded(){
    return this.openActionItemsCount==1 && !this.hasFinancialAuthorization;
  }

  get needsPrePurchaseAgreement(){
    return this.property?.auction?.purchaseAgreementTemplateId && this.property?.auction?.purchaseAgreementTemplateId>0;
  }

  get hasPrePurchaseAgreement(){
    return this.authorizationStatus?.purchaseAgreementExists;
  }

  get prePurchaseAgreementExpanded(){
    return this.openActionItemsCount==1 && !this.hasPrePurchaseAgreement;
  }

  //all action items done: nothing left to do here (the remaining item expands by itself otherwise)
  private closeIfComplete(){
    if(this.openActionItemsCount==0) this.dialogRef.close(true);
  }

  public onCancelClick(): void {
    this.dialogRef.close(false);
  }

  public onDownloadAgreementClick(){
    this.propertyService.getVaultDocumentsWithFile(undefined,"auction",this.property.auction!.id,this.property.auction!.scheduleSeqNo,this.authService.currentUserValue!.id,false).pipe(take(1)).subscribe((vdArray:VaultDocument[])=>{
      let vd = vdArray?.find(vd=>vd.documentTypeId==5);
      if(vd){
        this.downloadFile(vd.file!, vd.filename!);
      }
      else{
        this.snackBar.open('No pre-purchase agreement found for this auction', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
    });
  }

  private downloadFile(file:File,fileName:string){
    const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(file);
      a.href = objectUrl;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(objectUrl);
  }

  public legalDocumentExecuted(result:VaultDocumentsResponse){
    if(result.success){
      this.authorizationStatus.purchaseAgreementExists=true;
      //the executed agreement shows in the property's documents right away (same property object as the page)
      if(result.vaultDocuments?.length) this.property.addDocument(VaultDocument.mapFromDTO(result.vaultDocuments[0]));
      this.snackBar.open('Document executed successfully', '×', {
        verticalPosition: 'top',
        duration: 5000 
      }); 
      this.closeIfComplete();
    }
    else{
      this.snackBar.open('Document execution failed: ' + result.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
    }
  }

  public financialAuthorizationExecuted(result:Result){
    if(result.success){
      this.authorizationStatus.financialAuthorizationExists=true;
      this.snackBar.open('Financial authorization successful', '×', {
        verticalPosition: 'top',
        duration: 5000 
      }); 
      this.closeIfComplete();
    }
    else{
      this.snackBar.open('Financial authorization failed: ' + result.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
    }
  }

}



